import { type FC, useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "antd-mobile";
import useSWR from "swr";
import clsx from "clsx";
import { useSnackbar } from "~/Provider/Snackbar";
import { IconSearch } from "~/components/Iconfont/Iconfont";
import TokenList from "~/components/TokenList/TokenList";
import { Chain, Token } from "~/types";
import ChainCapsuleTab, {
    FAVORITE,
} from "~/components/ChainCapsuleTab/ChainCapsuleTab";
import {
    RANKING_TYPES,
    DURATION_TYPES,
    ColumnSortType,
    RankingType,
    DurationType,
    DEFAULT_CHAIN,
} from "~/pages/constants";
import { sortTokenList } from "./utils";
import {
    fetchFavoriteTokens,
    fetchTokenRankingList,
} from "../../api/endpoints";
import SimpleSegmentedControl from "./SimpleSegmentedControl/SimpleSegmentedControl";
import { useNavigate } from "react-router";
import { TokenColumn } from "~/components/TokenList/TokenItem";
import SortedColumnHead from "~/components/SortedColumnHead/SortedColumnHead";
import FavoriteSuggestion from "./FavoriteSuggestion";
import { useScrollPositionRestore } from "~/hook/useScrollPositionRestore";
import NoResult from "~/components/NoResult";
import { useLocalStorage } from "~/hook/useStorage";
import { formatNumber, formatPrice, formatVolume } from "~/utils/number-format";
import { Page } from "~/components/Page/Page";
import SafeAreaStickyTop from "~/components/widget/SafeAreaTop/SafeAreaStickyTop";
import Share2EarnBtn from "~/components/Share2Earn/Share2EarnBtn";

const MarketPage: FC = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [sortedTokenList, setSortedTokenList] = useState<Array<Token>>([]);
    const [rankingType, setRankingType] = useState<number>(RankingType.Vol);
    const durationTypeCacheKey = `__uxuy_dex_cached_marketing_durtaion_type__`;
    const [durationType, setDurationType] = useLocalStorage(
        durationTypeCacheKey,
        DurationType.OneDay
    );
    const [priceSortType, setPriceSortType] = useState<ColumnSortType>(
        ColumnSortType.None
    );
    const [volumeSortType, setVolumeSortType] = useState<ColumnSortType>(
        ColumnSortType.None
    );

    const queryTokenList = useCallback((args: any) => {
        const [selectedChainId, rankingType, durationType] = args;
        if (!selectedChainId) return null;
        if (selectedChainId === FAVORITE) {
            return fetchFavoriteTokens(rankingType, durationType).then(
                (res) => res.data.list ?? []
            );
        }
        return fetchTokenRankingList(
            selectedChainId,
            rankingType,
            durationType
        ).then((res) => res.data.list ?? []);
    }, []);

    const [selectedChainId, setSelectedChainId] = useState(() => {
        const cachedId = sessionStorage.getItem(
            "__uxuy_dex_cached_selected_chain_id__"
        );
        return cachedId ?? DEFAULT_CHAIN.id;
    });

    const {
        isLoading: tokenListLoading,
        data: tokenList,
        mutate,
    } = useSWR(
        [selectedChainId, rankingType, durationType, "tokenRankingList"],
        queryTokenList,
        {
            keepPreviousData: true,
            onError() {
                snackbar.error(
                    "Can not load token list! Check your network connection!"
                );
            },
        }
    );

    const { savePosition, restorePosition } = useScrollPositionRestore();

    useEffect(() => {
        setSortedTokenList(
            sortTokenList(
                tokenList ?? [],
                priceSortType,
                rankingType,
                volumeSortType
            )
        );
        restorePosition();
    }, [tokenList, priceSortType, rankingType, volumeSortType]);

    const tokenColumns: Array<
        TokenColumn & {
            sortable: boolean;
            sortType: ColumnSortType;
        }
    > = useMemo(
        () => [
            {
                key: "name",
                label: `${t("name")} / ${rankingType === RankingType.Txn ? t("transaction") : t("volume")}`,
                sortable: true,
                sortType: volumeSortType,
                showHot: selectedChainId !== FAVORITE,
                value(token) {
                    return rankingType === RankingType.Txn
                        ? `${t("transaction")} ${formatNumber(token.token_market.txn)}`
                        : `${t("volume")} ${formatVolume(token.token_market.volume_value)}`;
                },
                change() {
                    return "";
                },
            },
            {
                key: "price",
                label: `${t("price")} / ${t("changes")}`,
                sortable: true,
                sortType: priceSortType,
                value(token) {
                    return formatPrice(token.token_market.price);
                },
                change(token) {
                    return token.token_market.change;
                },
            },
        ],
        [priceSortType, volumeSortType, rankingType, selectedChainId]
    );

    const onSortChange = (key: string, sort: ColumnSortType) => {
        if (key === "price") {
            setPriceSortType(sort);
            setVolumeSortType(ColumnSortType.None);
        } else if (key === "name") {
            setPriceSortType(ColumnSortType.None);
            setVolumeSortType(sort);
        }
    };

    const handleChainChange = (chain: Chain) => {
        setSelectedChainId(chain.id);
        sessionStorage.setItem(
            "__uxuy_dex_cached_selected_chain_id__",
            chain.id
        );
    };

    const isEmptyFavorite =
        !tokenListLoading &&
        selectedChainId === FAVORITE &&
        (!tokenList || tokenList.length === 0);
    const isEmptyList =
        !tokenListLoading &&
        selectedChainId !== FAVORITE &&
        (!tokenList || tokenList.length === 0);

    const handleAddedFavoriteTokens = () => {
        mutate(tokenList, { revalidate: true });
    };

    const handleTokenClick = (token: Token) => {
        savePosition();
        navigate(
            `/k-detail?chain_id=${token.token_base.chain_id}&contract=${token.token_base.contract}`
        );
    };

    return (
        <Page>
            <>
                <div className="h-16 px-4 pt-[18px] pb-[10px] flex items-center justify-between">
                    <span className="text-h3 text-text_1 font-semibold">
                        {t("tab_bar_market")}
                    </span>
                    <div className="flex items-center">
                        <Button
                            style={
                                {
                                    height: 36,
                                    padding: 7,
                                    "--border-radius": "8px",
                                    "--border-color": "var(--color_border_2)",
                                    "--border-width": "0.5px",
                                    "--background-color": "transparent",
                                } as React.CSSProperties
                            }
                            onClick={() =>
                                navigate(
                                    `/token-search?chain_id=${selectedChainId}`
                                )
                            }
                        >
                            <IconSearch className="text-h5 text-text_2 " />
                        </Button>
                        <Share2EarnBtn className="ml-1.5" />
                    </div>
                </div>
                <ChainCapsuleTab
                    chainId={selectedChainId}
                    onChainChange={handleChainChange}
                />
                <SafeAreaStickyTop className="bg-bg_0" offsetTop="0.5px">
                    {!isEmptyFavorite && (
                        <>
                            <div className="h-11 px-4 flex justify-between items-center">
                                <SimpleSegmentedControl
                                    className="w-[80px]"
                                    options={RANKING_TYPES.map((item) => ({
                                        label: t(item.key),
                                        value: item.id,
                                    }))}
                                    selectedValue={rankingType}
                                    onSelectedValueChange={(type) => {
                                        setRankingType(type as RankingType);
                                        setVolumeSortType(ColumnSortType.None);
                                        setPriceSortType(ColumnSortType.None);
                                    }}
                                />
                                <SimpleSegmentedControl
                                    className="w-[160px]"
                                    options={DURATION_TYPES.map((item) => ({
                                        label: item.key,
                                        value: item.id,
                                    }))}
                                    selectedValue={durationType}
                                    onSelectedValueChange={(type) => {
                                        setDurationType(type as DurationType);
                                        setVolumeSortType(ColumnSortType.None);
                                        setPriceSortType(ColumnSortType.None);
                                    }}
                                />
                            </div>
                            <div className="flex-none h-8 flex items-center">
                                {tokenColumns.map((column, index, list) => {
                                    const isFirst = index === 0;
                                    const isLast = index === list.length - 1;
                                    return (
                                        <div
                                            key={index}
                                            className={clsx(
                                                "flex-1 w-0 flex min-w-20",
                                                isFirst && "pl-4",
                                                isLast && "pr-4 justify-end"
                                            )}
                                        >
                                            <SortedColumnHead
                                                label={column.label}
                                                sort={column.sortType}
                                                onClick={(sort) =>
                                                    onSortChange?.(
                                                        column.key,
                                                        sort
                                                    )
                                                }
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </SafeAreaStickyTop>
                {isEmptyFavorite && (
                    <FavoriteSuggestion
                        afterAdded={handleAddedFavoriteTokens}
                    />
                )}
                {isEmptyList && <NoResult />}
                <TokenList
                    isLoading={tokenListLoading}
                    tokens={sortedTokenList}
                    columns={tokenColumns}
                    showHead={false}
                    layout="Page"
                    onTokenClick={handleTokenClick}
                />
            </>
        </Page>
    );
};

export default MarketPage;
